// ============================================================================
// Beacon — transactional email (Resend).
//
// Same split as the analyzer: the branded email is BUILT here (pure, tested),
// the Resend API key lives in the `notify` Edge Function, a thin relay. Used to
// send a one-page investor memo or a gate-fail alert.
// ============================================================================

import type { Deal, Metrics } from "../types";
import { calc } from "../engine/engine";
import { money0, pct, x2 } from "./format";
import { isCloud, getSupabase } from "./supabase";
import { logUsage } from "./usage";

export interface Email {
  subject: string;
  html: string;
}

const esc = (s: string) => s.replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]!));

/** Pure: a branded one-page investor memo email for a deal. */
export function buildMemoEmail(deal: Deal, m: Metrics): Email {
  const s = m.score;
  const band = s.gated ? `${s.band}` : `${s.band} · ${s.composite}/100`;
  const bandColor = s.cls === "pass" ? "#3E7A5E" : s.cls === "watch" ? "#B08948" : "#A6503C";
  const row = (l: string, v: string) =>
    `<tr><td style="padding:6px 0;color:#6A7A72;font-size:13px">${esc(l)}</td><td style="padding:6px 0;text-align:right;font-weight:600;color:#17352B;font-size:13px">${esc(v)}</td></tr>`;
  return {
    subject: `Beacon memo — ${deal.name} (${band})`,
    html: `<div style="font-family:Inter,system-ui,sans-serif;max-width:560px;margin:0 auto;color:#1A211E">
  <div style="background:#17352B;color:#F5F1E8;padding:18px 22px;border-radius:12px 12px 0 0">
    <div style="font-size:12px;letter-spacing:.14em;text-transform:uppercase;color:#C8A45E">Investment Memorandum</div>
    <div style="font-family:Georgia,serif;font-size:22px;margin-top:4px">${esc(deal.name)}</div>
    <div style="font-size:13px;color:#CCD8CC;margin-top:2px">${esc(deal.addr || deal.market)}</div>
  </div>
  <div style="border:1px solid #DED7C6;border-top:none;border-radius:0 0 12px 12px;padding:22px;background:#FBF9F3">
    <div style="display:inline-block;background:${bandColor};color:#fff;font-weight:700;font-size:13px;padding:5px 12px;border-radius:100px">${esc(band)}</div>
    <table style="width:100%;border-collapse:collapse;margin-top:16px">
      ${row("All-in cost", money0(m.allIn))}
      ${row("Stabilized value", money0(m.stabVal))}
      ${row("NOI (stabilized)", money0(m.noi))}
      ${row("Cap rate (on cost)", pct(m.capOnCost))}
      ${row("DSCR", x2(m.dscr) + " (min " + x2(deal.dscrMin) + ")")}
      ${row("Cash-on-cash", pct(m.coc))}
      ${row("Proj. LP IRR (5-yr)", m.lpIRR ? pct(m.lpIRR) : "—")}
      ${row("Insurance-stress DSCR", x2(m.dscrStress))}
    </table>
    <p style="font-size:11px;color:#6A7A72;margin-top:18px;line-height:1.5">Decision-support, not investment advice. Illustrative figures from the Beacon underwriting engine. Bridge-to-permanent; takeout to agency at stabilization.</p>
  </div>
</div>`,
  };
}

/** Send the memo email. Cloud-only: the Resend key lives in the Edge Function. */
export async function emailMemo(deal: Deal, to: string): Promise<void> {
  if (!isCloud()) throw new Error("Email needs cloud mode (the Resend key lives in the Edge Function).");
  const sb = getSupabase()!;
  const { subject, html } = buildMemoEmail(deal, calc(deal));
  const { error } = await sb.functions.invoke("notify", { body: { to, subject, html } });
  if (error) throw new Error(error.message || "email failed");
  void logUsage("Email (Resend)", "investor memo");
}

// --- offer / negotiation letter delivery (sent as APAS Build) ----------------

/** Pure: render a plain-text letter (Claude output) into clean business-letter
 *  HTML — paragraphs, "- " bullets, and **bold** — wrapped in APAS Build branding. */
export function letterToHtml(text: string): string {
  const inline = (s: string) => esc(s).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  // Within each block, group consecutive "- " lines into a <ul>, the rest into <p>.
  const renderBlock = (b: string): string => {
    const lines = b.split(/\n/).map((l) => l.trim()).filter(Boolean);
    let out = "";
    let para: string[] = [];
    let bullets: string[] = [];
    const flushPara = () => { if (para.length) { out += `<p style="margin:0 0 14px;line-height:1.6">${para.map(inline).join("<br>")}</p>`; para = []; } };
    const flushBullets = () => { if (bullets.length) { out += `<ul style="margin:0 0 14px;padding-left:20px">${bullets.map((l) => `<li style="margin:4px 0">${inline(l)}</li>`).join("")}</ul>`; bullets = []; } };
    for (const l of lines) {
      if (/^[-*]\s+/.test(l)) { flushPara(); bullets.push(l.replace(/^[-*]\s+/, "")); }
      else { flushBullets(); para.push(l); }
    }
    flushPara(); flushBullets();
    return out;
  };
  const body = text.trim().split(/\n\s*\n/).map(renderBlock).join("");

  return `<div style="font-family:Georgia,'Times New Roman',serif;max-width:600px;margin:0 auto;color:#1A211E;font-size:15px">
  <div style="padding:0 0 14px;border-bottom:2px solid #B08948;margin-bottom:22px">
    <div style="font-family:Inter,system-ui,sans-serif;font-size:16px;font-weight:700;letter-spacing:.02em;color:#17352B">APAS Build</div>
    <div style="font-family:Inter,system-ui,sans-serif;font-size:11px;letter-spacing:.12em;text-transform:uppercase;color:#8A6A2E;margin-top:2px">Acquisitions</div>
  </div>
  ${body}
  <div style="border-top:1px solid #DED7C6;margin-top:22px;padding-top:12px;font-family:Inter,system-ui,sans-serif;font-size:11px;color:#6A7A72;line-height:1.5">
    APAS Build · apas.dev — supporting documentation available on request. This letter is a good-faith negotiation communication, not a binding contract.
  </div>
</div>`;
}

/** Pure: subject + branded HTML for an offer letter email. */
export function buildOfferLetterEmail(deal: Deal, letterText: string, subject?: string): Email {
  return {
    subject: subject?.trim() || `Revised offer — ${deal.name}${deal.addr ? ` (${deal.addr})` : ""}`,
    html: letterToHtml(letterText),
  };
}

/** Send the offer letter as APAS Build. Cloud-only (Resend key in the Edge Function). */
export async function sendOfferLetter(deal: Deal, to: string, letterText: string, subject?: string): Promise<void> {
  if (!isCloud()) throw new Error("Email needs cloud mode (the Resend key lives in the Edge Function).");
  const sb = getSupabase()!;
  const email = buildOfferLetterEmail(deal, letterText, subject);
  const { error } = await sb.functions.invoke("notify", { body: { to, subject: email.subject, html: email.html } });
  if (error) throw new Error(error.message || "email failed");
  void logUsage("Email (Resend)", "offer letter");
}
